import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Star, EyeOff, Flame, Download } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { logUpdate } from '@/lib/audit-log';
import { DataPagination } from '@/components/DataPagination';

export default function Submissions() {
  const queryClient = useQueryClient();
  const [dayFilter, setDayFilter] = useState<string>('all');
  const [editingScore, setEditingScore] = useState<{ id: string; score: number } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [exporting, setExporting] = useState(false);

  const { data: submissionsData } = useQuery({
    queryKey: ['submissions', dayFilter, currentPage, pageSize],
    queryFn: async () => {
      let q = supabase
        .from('challenge_submissions')
        .select('*, profiles(email, full_name, tiktok_handle)', { count: 'exact' })
        .order('created_at', { ascending: false });
      
      if (dayFilter !== 'all') q = q.eq('challenge_day', parseInt(dayFilter));
      
      q = q.range((currentPage - 1) * pageSize, currentPage * pageSize - 1);
      
      const { data, count, error } = await q;
      if (error) throw error;
      
      return { data: data ?? [], total: count ?? 0 };
    },
  });

  const submissions = submissionsData?.data ?? [];
  const totalSubmissions = submissionsData?.total ?? 0;
  const totalPages = Math.ceil(totalSubmissions / pageSize);

  const handleExport = async () => {
    setExporting(true);
    try {
      const BATCH = 1000;
      let allData: any[] = [];
      let from = 0;

      while (true) {
        let q = supabase
          .from('challenge_submissions')
          .select('*, profiles(email, full_name, tiktok_handle)')
          .order('created_at', { ascending: false })
          .range(from, from + BATCH - 1);

        if (dayFilter !== 'all') q = q.eq('challenge_day', parseInt(dayFilter));

        const { data: batch, error } = await q;
        if (error) throw error;
        if (!batch || batch?.length === 0) break;

        allData = allData?.concat(batch);
        if (batch.length < BATCH) break;
        from += BATCH;
      }

      if (allData?.length === 0) return;

      const headers = ['id', 'flame_score', 'is_featured', 'is_hidden', 'email', 'full_name', 'tiktok_handle', 'video_url', 'thumbnail_url', 'created_at'];
      const csvRows = [
        headers.join(','),
        ...allData.map((row: any) => {
          const profile = row.profiles ?? {};
          const values = [
            row?.id, row?.flame_score, row?.is_featured, row?.is_hidden,
            profile?.email, profile?.full_name, profile?.tiktok_handle,
            row?.video_url, row?.thumbnail_url ?? row?.video_thumbnail_url, row?.created_at,
          ];
          return values.map((v) => {
            const val = String(v ?? '');
            return val.includes(',') || val.includes('"') || val.includes('\n')
              ? `"${val.replace(/"/g, '""')}"`
              : val;
          }).join(',');
        }),
      ];

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const suffix = dayFilter !== 'all' ? `_day${dayFilter}` : '';
      link.download = `submissions${suffix}_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, any> }) => {
      // Fetch old values for audit log
      const { data: oldData } = await supabase.from('challenge_submissions').select('*').eq('id', id).single();
      const { data, error } = await supabase.from('challenge_submissions').update(updates).eq('id', id).select().single();
      if (error) throw error;
      await logUpdate('challenge_submissions', id, oldData, data, {
        description: `Updated submission ${Object.keys(updates).join(', ')}`,
        updates,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      toast.success('Updated');
    },
    onError: (err: any) => toast.error(err.message),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Submissions Moderation</h1>
        <div className="flex items-center gap-2">
          {/* <Select value={dayFilter} onValueChange={(value) => { setCurrentPage(1); setDayFilter(value); }}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Filter by day" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Days</SelectItem>
              {Array.from({ length: 30 }, (_, i) => (
                <SelectItem key={i + 1} value={String(i + 1)}>Day {i + 1}</SelectItem>
              ))}
            </SelectContent>
          </Select> */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting || totalSubmissions === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {submissions?.map((s: any) => {
          const name = s.profiles?.name || s.profiles?.tiktok_handle || s.profiles?.email || 'Unknown';
          return (
            <Card key={s.id} className="overflow-hidden">
              <div className="aspect-video bg-muted flex items-center justify-center relative">
                {s.thumbnail_url || s.video_thumbnail_url ? (
                  <img src={s.thumbnail_url || s.video_thumbnail_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-muted-foreground text-sm">No thumbnail</span>
                )}
                {s.is_featured && (
                  <Badge className="absolute top-2 right-2 gap-1"><Star className="h-3 w-3" /> Featured</Badge>
                )}
              </div>
              <CardContent className="p-3 space-y-2">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-sm">{name}</p>
                    <p className="text-xs text-muted-foreground">Day {s.challenge_day ?? s.day_number} · {s.created_at && format(new Date(s.created_at), 'MMM d')}</p>
                  </div>
                  <div className="flex items-center gap-1 text-sm">
                    <Flame className="h-3 w-3" />
                    {editingScore?.id === s.id ? (
                      <Input
                        type="number"
                        className="h-6 w-16 text-xs"
                        value={editingScore.score}
                        onChange={e => setEditingScore({ ...editingScore, score: +e.target.value })}
                        onBlur={() => {
                          updateMutation.mutate({ id: s.id, updates: { flame_score: editingScore.score } });
                          setEditingScore(null);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            updateMutation.mutate({ id: s.id, updates: { flame_score: editingScore.score } });
                            setEditingScore(null);
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <span className="cursor-pointer" onClick={() => setEditingScore({ id: s.id, score: s.flame_score ?? 0 })}>{s.flame_score ?? 0}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant={s.is_featured ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1 text-xs h-7"
                      >
                        <Star className="mr-1 h-3 w-3" /> {s.is_featured ? 'Unfeature' : 'Feature'}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{s.is_featured ? 'Unfeature' : 'Feature'} this submission?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {s.is_featured 
                            ? 'This submission will no longer be marked as featured.' 
                            : 'This submission will be marked as featured and highlighted to users.'}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => updateMutation.mutate({ id: s.id, updates: { is_featured: !s.is_featured } })}>
                          Confirm
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant={s.is_hidden ? 'destructive' : 'outline'}
                        size="sm"
                        className="flex-1 text-xs h-7"
                      >
                        <EyeOff className="mr-1 h-3 w-3" /> {s.is_hidden ? 'Unhide' : 'Hide'}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{s.is_hidden ? 'Unhide' : 'Hide'} this submission?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {s.is_hidden 
                            ? 'This submission will be visible to users again.' 
                            : 'This submission will be hidden from users.'}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => updateMutation.mutate({ id: s.id, updates: { is_hidden: !s.is_hidden } })}
                          className={s.is_hidden ? '' : 'bg-destructive text-destructive-foreground hover:bg-destructive/90'}
                        >
                          Confirm
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      {submissions?.length === 0 && <p className="text-center text-muted-foreground py-12">No submissions found</p>}
      {submissions && submissions?.length > 0 && (
        <DataPagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalSubmissions}
          onPageChange={setCurrentPage}
          onPageSizeChange={(newSize) => { setCurrentPage(1); setPageSize(newSize); }}
        />
      )}
    </div>
  );
}
